/**
 * NovelStudio Motion Primitives (WP4)
 *
 * Reusable animation wrappers using framer-motion.
 * Keep durations ≤ 300ms for snappy feel, stagger ≤ 50ms.
 * RA-01: All animations respect prefers-reduced-motion via MotionConfig.
 */
import { motion, MotionConfig, type Variants, type HTMLMotionProps, AnimatePresence } from 'framer-motion';
import { forwardRef, type ReactNode } from 'react';

/**
 * ReducedMotionProvider — Wrap your app to respect prefers-reduced-motion (RA-01)
 *
 * When reducedMotion="user", framer-motion reads the OS/browser
 * prefers-reduced-motion setting and disables animations accordingly.
 */
export function ReducedMotionProvider({ children }: { children: ReactNode }) {
  return (
    <MotionConfig reducedMotion="user">
      {children}
    </MotionConfig>
  );
}

/* ── Shared easing ──────────────────────────── */
const ease = [0.33, 1, 0.68, 1] as const; // cubic-bezier for natural decel

/* ── FadeIn: simple opacity entrance ─────────── */
export function FadeIn({
  children,
  delay = 0,
  duration = 0.3,
  className,
}: {
  children: ReactNode;
  delay?: number;
  duration?: number;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ── SlideUp: fade + slide from bottom ───────── */
export function SlideUp({
  children,
  delay = 0,
  y = 16,
  duration = 0.4,
  className,
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  duration?: number;
  className?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration, delay, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

/* ── StaggerContainer + StaggerItem: grid entrance ── */
const staggerContainerVariants: Variants = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: 0.05,
    },
  },
};

const staggerItemVariants: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease } },
};

export function StaggerContainer({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <motion.div
      variants={staggerContainerVariants}
      initial="hidden"
      animate="show"
      className={className}
    >
      {children}
    </motion.div>
  );
}

export function StaggerItem({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <motion.div variants={staggerItemVariants} className={className}>
      {children}
    </motion.div>
  );
}

/* ── ScalePress: button micro-interaction ────── */
export const ScalePress = forwardRef<HTMLDivElement, HTMLMotionProps<'div'> & { children: ReactNode }>(
  ({ children, ...props }, ref) => (
    <motion.div
      ref={ref}
      whileTap={{ scale: 0.97 }}
      whileHover={{ scale: 1.01 }}
      transition={{ duration: 0.15 }}
      {...props}
    >
      {children}
    </motion.div>
  ),
);
ScalePress.displayName = 'ScalePress';

/* ── PageTransition: route-level wrapper ─────── */
export function PageTransition({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25, ease }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

export { AnimatePresence };